/**
 * ProjectTasksList Component
 *
 * Renders project tasks with responsive layout:
 * - Desktop: Full-featured table with columns
 * - Mobile: Card-based list for better touch interaction
 *
 * Features:
 * - Search filtering
 * - Row/card selection for bulk actions
 * - Pagination
 * - Remove task action
 */

import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Table, Badge, Tooltip, Button, Tag, Input, Empty, Pagination, Select, DatePicker, message } from 'antd';
import {
  DeleteOutlined,
  SearchOutlined,
  CloseOutlined,
  CalendarOutlined,
  RightOutlined,
  LeftOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { Link, useHistory } from 'react-router-dom';
import { startCase } from 'lodash';
import moment from 'moment';
import AvatarS3 from '../../../../shared/images/AvatarS3';
import TaskMobileCards from '../shared/TaskMobileCards';
import { urgencyColors, statusTypes, urgencyTypes } from '../../configs/task.config';
import { space } from '../../configs/designTokens';
import { useTaskContext } from '../../../../../core/components/tasks/useTaskContext';
import { useProjectContext } from '../../../../../core/components/projects/useProjectContext';
import { getStatusConfig } from '../../utils/taskTableUtils';
import '../../styles/index.scss';

const InlineStatusCell = ({ taskId, currentStatus }) => {
  const { updateTask } = useTaskContext();
  const { refetchProject } = useProjectContext();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleChange = async (newStatus) => {
    if (newStatus === currentStatus) {
      setIsEditing(false);
      return;
    }
    setSaving(true);
    try {
      await updateTask({
        variables: {
          data: { status: newStatus, activityLog: 'Updated Status' },
          where: { id: taskId },
        },
      });
      message.success('Task status updated.');
      await refetchProject();
      setIsEditing(false);
    } catch (err) {
      message.error('Failed to update task status.');
    } finally {
      setSaving(false);
    }
  };

  if (isEditing) {
    return (
      <Select
        autoFocus
        defaultOpen
        size="small"
        value={currentStatus || undefined}
        loading={saving}
        disabled={saving}
        onChange={handleChange}
        onBlur={() => !saving && setIsEditing(false)}
        style={{ width: '100%', minWidth: 140 }}
        onClick={(e) => e.stopPropagation()}
      >
        {Object.entries(statusTypes).map(([label, val]) => (
          <Select.Option key={val} value={val}>
            {label}
          </Select.Option>
        ))}
      </Select>
    );
  }

  const handleActivate = (e) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleActivate(e);
    }
  };

  const config = getStatusConfig(currentStatus);
  const isEmpty = !currentStatus;

  return (
    <div
      className={`project-tasks__editable${isEmpty ? ' project-tasks__editable--empty' : ''}`}
      role="button"
      tabIndex={0}
      aria-label="Edit status"
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
    >
      {currentStatus ? (
        <Tag color={config.color} style={{ margin: 0 }}>
          {config.label}
        </Tag>
      ) : (
        <span className="project-tasks__empty-value">N/A</span>
      )}
      <EditOutlined className="project-tasks__editable-icon" />
    </div>
  );
};

InlineStatusCell.propTypes = {
  taskId: PropTypes.string.isRequired,
  currentStatus: PropTypes.string,
};

const InlineDueDateCell = ({ taskId, currentDueDate }) => {
  const { updateTask } = useTaskContext();
  const { refetchProject } = useProjectContext();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleChange = async (newMoment) => {
    const newIso = newMoment ? newMoment.toISOString() : null;
    const currentIso = currentDueDate || null;
    if (newIso === currentIso) {
      setIsEditing(false);
      return;
    }
    setSaving(true);
    try {
      await updateTask({
        variables: {
          data: { dueDate: newIso, activityLog: 'Updated Details' },
          where: { id: taskId },
        },
      });
      message.success('Due date updated.');
      await refetchProject();
      setIsEditing(false);
    } catch (err) {
      message.error('Failed to update due date.');
    } finally {
      setSaving(false);
    }
  };

  if (isEditing) {
    return (
      <DatePicker
        autoFocus
        open
        size="small"
        value={currentDueDate ? moment(currentDueDate) : undefined}
        disabled={saving}
        onChange={handleChange}
        onOpenChange={(open) => !open && !saving && setIsEditing(false)}
        style={{ width: '100%', minWidth: 140 }}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  const handleActivate = (e) => {
    e.stopPropagation();
    setIsEditing(true);
  };
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleActivate(e);
    }
  };

  const isEmpty = !currentDueDate;
  let display;
  if (isEmpty) {
    display = <span className="project-tasks__empty-value">-</span>;
  } else {
    const due = moment(currentDueDate);
    const daysLeft = due.diff(moment(), 'days');
    let className = 'project-tasks__due-date';
    if (daysLeft < 0) className += ' project-tasks__due-date--overdue';
    else if (daysLeft <= 3) className += ' project-tasks__due-date--soon';
    display = <span className={className}>{due.format('MMM D, YYYY')}</span>;
  }

  return (
    <div
      className={`project-tasks__editable${isEmpty ? ' project-tasks__editable--empty' : ''}`}
      role="button"
      tabIndex={0}
      aria-label="Edit due date"
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
    >
      {display}
      <EditOutlined className="project-tasks__editable-icon" />
    </div>
  );
};

InlineDueDateCell.propTypes = {
  taskId: PropTypes.string.isRequired,
  currentDueDate: PropTypes.string,
};

const InlineAssigneeCell = ({ taskId, assigneeUsers }) => {
  const { updateTask } = useTaskContext();
  const { refetchProject, currentProject } = useProjectContext();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const userPool = useMemo(() => {
    const pool = [];
    if (currentProject?.owner) pool.push(currentProject.owner);
    (currentProject?.team || []).forEach((u) => {
      if (!pool.find((p) => p.id === u.id)) pool.push(u);
    });
    return pool;
  }, [currentProject]);

  const currentAssigneeIds = (assigneeUsers || []).map((u) => u.id);
  const currentValue = currentAssigneeIds[0] || undefined;

  const handleChange = async (newAssigneeId) => {
    const next = newAssigneeId || null;
    const cur = currentValue || null;
    if (next === cur) {
      setIsEditing(false);
      return;
    }
    setSaving(true);
    try {
      const op = {};
      if (currentAssigneeIds.length > 0) {
        const disconnectIds = currentAssigneeIds.filter((id) => id !== next);
        if (disconnectIds.length > 0) op.disconnect = disconnectIds.map((id) => ({ id }));
      }
      if (next && !currentAssigneeIds.includes(next)) {
        op.connect = { id: next };
      }

      await updateTask({
        variables: {
          data: { assigneeUsers: op, activityLog: 'Updated Assignee' },
          where: { id: taskId },
        },
      });
      message.success('Assignee updated.');
      await refetchProject();
      setIsEditing(false);
    } catch (err) {
      message.error('Failed to update assignee.');
    } finally {
      setSaving(false);
    }
  };

  if (isEditing) {
    return (
      <Select
        autoFocus
        defaultOpen
        size="small"
        value={currentValue}
        loading={saving}
        disabled={saving}
        onChange={handleChange}
        onBlur={() => !saving && setIsEditing(false)}
        style={{ minWidth: 180 }}
        onClick={(e) => e.stopPropagation()}
        allowClear
        placeholder="Assignee"
        showSearch
        optionFilterProp="children"
      >
        {userPool.map((u) => (
          <Select.Option key={u.id} value={u.id}>
            {startCase(u.name || u.username)}
          </Select.Option>
        ))}
      </Select>
    );
  }

  const handleActivate = (e) => {
    e.stopPropagation();
    setIsEditing(true);
  };
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleActivate(e);
    }
  };

  const isEmpty = !assigneeUsers || assigneeUsers.length === 0;
  let display;
  if (isEmpty) {
    display = <span className="project-tasks__empty-value">-</span>;
  } else {
    display = (
      <div className="project-tasks__assignees">
        {assigneeUsers.slice(0, 2).map((user) => (
          <Tooltip key={user.id} title={startCase(user.name || user.username)}>
            <div className="project-tasks__assignee-avatar">
              <AvatarS3
                imgKey={user.avatar?.key}
                username={user.name || user.username}
                avatarProps={{ size: 26 }}
              />
            </div>
          </Tooltip>
        ))}
        {assigneeUsers.length > 2 && (
          <span className="project-tasks__assignee-more">
            +{assigneeUsers.length - 2}
          </span>
        )}
      </div>
    );
  }

  return (
    <div
      className={`project-tasks__editable${isEmpty ? ' project-tasks__editable--empty' : ''}`}
      role="button"
      tabIndex={0}
      aria-label="Edit assignee"
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
    >
      {display}
      <EditOutlined className="project-tasks__editable-icon" />
    </div>
  );
};

InlineAssigneeCell.propTypes = {
  taskId: PropTypes.string.isRequired,
  assigneeUsers: PropTypes.array,
};

const InlineMilestoneCell = ({ taskId, currentMilestone, milestones }) => {
  const { updateMilestone, fetchMilestones, refetchProject, currentProject } = useProjectContext();
  const projectId = currentProject?.id;
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleChange = async (newMilestoneId) => {
    const fromId = currentMilestone?.id || null;
    const toId = newMilestoneId || null;
    if (fromId === toId) {
      setIsEditing(false);
      return;
    }
    setSaving(true);
    try {
      if (fromId) {
        await updateMilestone({
          variables: {
            data: { tasks: { disconnect: [{ id: taskId }] } },
            where: { id: fromId },
          },
        });
      }
      if (toId) {
        await updateMilestone({
          variables: {
            data: { tasks: { connect: [{ id: taskId }] } },
            where: { id: toId },
          },
        });
      }

      message.success('Milestone updated.');
      await Promise.all([
        refetchProject(),
        projectId
          ? fetchMilestones({
              variables: {
                where: { project: { id: projectId } },
                orderBy: 'dueDate_ASC',
              },
            })
          : Promise.resolve(),
      ]);
      setIsEditing(false);
    } catch (err) {
      message.error('Failed to update milestone.');
    } finally {
      setSaving(false);
    }
  };

  if (isEditing) {
    return (
      <Select
        autoFocus
        defaultOpen
        size="small"
        value={currentMilestone?.id || undefined}
        loading={saving}
        disabled={saving}
        onChange={handleChange}
        onBlur={() => !saving && setIsEditing(false)}
        style={{ minWidth: 160 }}
        onClick={(e) => e.stopPropagation()}
        allowClear
        placeholder="Milestone"
      >
        {(milestones || []).map((m) => (
          <Select.Option key={m.id} value={m.id}>
            {m.name}
          </Select.Option>
        ))}
      </Select>
    );
  }

  const handleActivate = (e) => {
    e.stopPropagation();
    setIsEditing(true);
  };
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleActivate(e);
    }
  };

  const isEmpty = !currentMilestone;

  return (
    <div
      className={`project-tasks__editable${isEmpty ? ' project-tasks__editable--empty' : ''}`}
      role="button"
      tabIndex={0}
      aria-label="Edit milestone"
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
    >
      {currentMilestone ? (
        <Tag color="blue" className="project-tasks__milestone-tag" style={{ margin: 0 }}>
          {currentMilestone.name}
        </Tag>
      ) : (
        <span className="project-tasks__empty-value">-</span>
      )}
      <EditOutlined className="project-tasks__editable-icon" />
    </div>
  );
};

InlineMilestoneCell.propTypes = {
  taskId: PropTypes.string.isRequired,
  currentMilestone: PropTypes.object,
  milestones: PropTypes.array,
};

const InlineUrgencyCell = ({ taskId, currentUrgency }) => {
  const { updateTask } = useTaskContext();
  const { refetchProject } = useProjectContext();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleChange = async (newUrgency) => {
    const next = newUrgency || null;
    const cur = currentUrgency || null;
    if (next === cur) {
      setIsEditing(false);
      return;
    }
    setSaving(true);
    try {
      await updateTask({
        variables: {
          data: { urgency: next, activityLog: 'Updated Details' },
          where: { id: taskId },
        },
      });
      message.success('Urgency updated.');
      await refetchProject();
      setIsEditing(false);
    } catch (err) {
      message.error('Failed to update urgency.');
    } finally {
      setSaving(false);
    }
  };

  if (isEditing) {
    return (
      <Select
        autoFocus
        defaultOpen
        size="small"
        value={currentUrgency || undefined}
        loading={saving}
        disabled={saving}
        onChange={handleChange}
        onBlur={() => !saving && setIsEditing(false)}
        style={{ minWidth: 110 }}
        onClick={(e) => e.stopPropagation()}
        allowClear
        placeholder="Urgency"
      >
        {Object.entries(urgencyTypes).map(([label, val]) => (
          <Select.Option key={val} value={val}>
            {label}
          </Select.Option>
        ))}
      </Select>
    );
  }

  const handleActivate = (e) => {
    e.stopPropagation();
    setIsEditing(true);
  };
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleActivate(e);
    }
  };

  return (
    <span
      className="project-tasks__editable"
      role="button"
      tabIndex={0}
      aria-label={
        currentUrgency
          ? `Urgency: ${startCase(currentUrgency.toLowerCase())}. Click to edit.`
          : 'Set urgency'
      }
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
      style={{ padding: '2px 4px' }}
    >
      <Tooltip
        title={
          currentUrgency
            ? `Urgency: ${startCase(currentUrgency.toLowerCase())}`
            : 'Set urgency'
        }
      >
        <Badge
          dot
          color={urgencyColors[currentUrgency] || '#d9d9d9'}
          className="project-tasks__urgency-badge"
        />
      </Tooltip>
    </span>
  );
};

InlineUrgencyCell.propTypes = {
  taskId: PropTypes.string.isRequired,
  currentUrgency: PropTypes.string,
};


const ProjectTasksList = ({
  tasks,
  milestones,
  selectedTaskIds,
  onSelectionChange,
  onRemoveTask,
  isMobile,
  isTablet,
}) => {
  const history = useHistory();
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Filter tasks by search
  const filteredTasks = useMemo(() => {
    if (!searchText) return tasks;
    return tasks.filter((task) =>
      task.name.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [tasks, searchText]);

  // Get milestone for a task
  const getTaskMilestone = (taskId) => {
    return (milestones || []).find((m) =>
      m.tasks?.some((t) => t.id === taskId)
    );
  };

  // Paginated tasks for mobile cards (client-side pagination)
  const paginatedTasks = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredTasks.slice(startIndex, startIndex + pageSize);
  }, [filteredTasks, currentPage, pageSize]);

  const handleMobileTaskClick = (taskId) => {
    history.push(`/tasks/details/${taskId}`);
  };

  // Mobile Card View
  const renderMobileCards = () => (
    <>
      <TaskMobileCards
        tasks={paginatedTasks}
        onTaskClick={handleMobileTaskClick}
        selectedIds={selectedTaskIds}
        onSelectionChange={onSelectionChange}
        onRemoveTask={onRemoveTask}
        milestones={milestones}
      />

      {/* Mobile Pagination */}
      {filteredTasks.length > 0 && (
        <div className="task-mobile-pagination">
          <div className="task-mobile-pagination__progress">
            <div className="task-mobile-pagination__progress-bar">
              <div
                className="task-mobile-pagination__progress-fill"
                style={{ width: `${Math.min(100, (Math.min(currentPage * pageSize, filteredTasks.length) / filteredTasks.length) * 100)}%` }}
              />
            </div>
            <span className="task-mobile-pagination__progress-text">
              {Math.min(currentPage * pageSize, filteredTasks.length)} of {filteredTasks.length}
            </span>
          </div>
          {filteredTasks.length > pageSize && (
            <div style={{ display: 'flex', gap: space[2] }}>
              <Button
                type="text"
                icon={<LeftOutlined />}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Button
                type="text"
                disabled={currentPage >= Math.ceil(filteredTasks.length / pageSize)}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                Next
                <RightOutlined />
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );

  // Desktop Table View
  const renderTable = () => {
    const columns = [
      {
        title: <span className="project-tasks__col-header">Task Name</span>,
        dataIndex: 'name',
        key: 'name',
        width: '35%',
        render: (text, record) => (
          <div className="project-tasks__name-cell">
            <InlineUrgencyCell taskId={record.id} currentUrgency={record.urgency} />
            <div className="project-tasks__name-content">
              <Link to={`/tasks/details/${record.id}`} className="project-tasks__name-link">
                {text}
              </Link>
              {record.requestType && (
                <span className="project-tasks__request-type">
                  {record.requestType.split('_').map((word) =>
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                  ).join(' ')}
                </span>
              )}
            </div>
          </div>
        ),
      },
      {
        title: <span className="project-tasks__col-header">Due Date</span>,
        dataIndex: 'dueDate',
        key: 'dueDate',
        width: '12%',
        render: (_, record) => (
          <InlineDueDateCell taskId={record.id} currentDueDate={record.dueDate} />
        ),
      },
      {
        title: <span className="project-tasks__col-header">Assignee</span>,
        dataIndex: 'assigneeUsers',
        key: 'assigneeUsers',
        width: '15%',
        render: (_, record) => (
          <InlineAssigneeCell taskId={record.id} assigneeUsers={record.assigneeUsers} />
        ),
      },
      {
        title: <span className="project-tasks__col-header">Milestone</span>,
        key: 'milestone',
        width: '20%',
        render: (_, record) => (
          <InlineMilestoneCell
            taskId={record.id}
            currentMilestone={getTaskMilestone(record.id)}
            milestones={milestones}
          />
        ),
      },
      {
        title: <span className="project-tasks__col-header">Status</span>,
        key: 'status',
        width: '16%',
        render: (_, record) => (
          <InlineStatusCell taskId={record.id} currentStatus={record.status} />
        ),
      },
      {
        title: '',
        key: 'actions',
        width: '60px',
        align: 'center',
        render: (_, record) => (
          <Tooltip title="Remove from project">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onRemoveTask(record.id, record.name);
              }}
              className="project-tasks__remove-btn"
            />
          </Tooltip>
        ),
      },
    ];

    const rowSelection = {
      selectedRowKeys: selectedTaskIds,
      preserveSelectedRowKeys: true,
      onChange: onSelectionChange,
      selections: [
        {
          key: 'select-all',
          text: 'Select All',
          onSelect: () => onSelectionChange(filteredTasks.map((t) => t.id)),
        },
        {
          key: 'clear-all',
          text: 'Clear All',
          onSelect: () => onSelectionChange([]),
        },
        Table.SELECTION_INVERT,
      ],
    };

    const total = filteredTasks.length;
    const paginationStart = total > 0 ? (currentPage - 1) * pageSize + 1 : 0;
    const paginationEnd = Math.min(currentPage * pageSize, total);

    const itemRender = (_page, type, originalElement) => {
      if (type === 'prev') {
        return (
          <Button type="text" icon={<LeftOutlined />}>
            Previous
          </Button>
        );
      }
      if (type === 'next') {
        return (
          <Button type="text">
            Next
            <RightOutlined />
          </Button>
        );
      }
      return originalElement;
    };

    return (
      <>
        <Table
          columns={columns}
          dataSource={filteredTasks}
          rowKey="id"
          rowSelection={rowSelection}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            onChange: (page) => setCurrentPage(page),
            showSizeChanger: false,
            className: 'project-tasks__table-pagination--hidden',
          }}
          scroll={{ x: 900 }}
          locale={{ emptyText: 'No tasks found' }}
          className="project-tasks__table"
        />

        {total > 0 && (
          <div className="task-pagination-enhanced">
            <div className="task-pagination-enhanced__left">
              <span className="task-pagination-enhanced__summary">
                Showing <strong>{paginationStart}</strong>
                {' - '}
                <strong>{paginationEnd}</strong>
                {' of '}
                <strong>{total}</strong> tasks
              </span>
            </div>

            <div className="task-pagination-enhanced__center">
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={total}
                showSizeChanger={false}
                hideOnSinglePage={false}
                onChange={(page) => setCurrentPage(page)}
                itemRender={itemRender}
                className="task-pagination-enhanced__nav"
                showLessItems={isTablet}
              />
            </div>

            <div className="task-pagination-enhanced__right">
              <span className="task-pagination-enhanced__size-label">Show:</span>
              <div className="task-pagination-enhanced__size-options">
                {[10, 25, 50, 100].map((size) => (
                  <button
                    key={size}
                    className={`task-pagination-enhanced__size-btn ${pageSize === size ? 'task-pagination-enhanced__size-btn--active' : ''}`}
                    onClick={() => {
                      setPageSize(size);
                      setCurrentPage(1);
                    }}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="project-tasks">
      {/* Search Bar */}
      <div className="project-tasks__search">
        <Input
          placeholder="Search tasks..."
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setCurrentPage(1);
          }}
          prefix={<SearchOutlined className="project-tasks__search-icon" />}
          suffix={
            searchText && (
              <CloseOutlined
                className="project-tasks__search-clear"
                onClick={() => setSearchText('')}
              />
            )
          }
          className="project-tasks__search-input"
        />
        {searchText && (
          <span className="project-tasks__search-count">
            {filteredTasks.length} result{filteredTasks.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* List/Table */}
      {isMobile ? renderMobileCards() : renderTable()}
    </div>
  );
};

ProjectTasksList.propTypes = {
  tasks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      status: PropTypes.string,
      urgency: PropTypes.string,
      dueDate: PropTypes.string,
      requestType: PropTypes.string,
      assigneeUsers: PropTypes.array,
    })
  ),
  milestones: PropTypes.array,
  selectedTaskIds: PropTypes.arrayOf(PropTypes.string),
  onSelectionChange: PropTypes.func.isRequired,
  onRemoveTask: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
  isTablet: PropTypes.bool,
};

ProjectTasksList.defaultProps = {
  tasks: [],
  milestones: [],
  selectedTaskIds: [],
  isMobile: false,
  isTablet: false,
};

export default ProjectTasksList;
